<!--
  Owned, elevated LEAD breadcrumb row. Precedence: [breadcrumbs] items >
  projection > auto route-walking. When a data list is resolved, the
  `variant` input selects the rendering treatment.

  Inline SVG glyphs (chevron / check / dots) are used directly here because
  the unified <tw-icon> system is icon-ref driven and the breadcrumb chrome
  needs a handful of fixed structural glyphs that must render in every theme
  without an IconStore round-trip. Colour comes from `currentColor`, so the
  glyphs follow the token-driven text colours like everything else.
-->

<!--
  Home glyph for the home-led leading crumb (default/chevron variant).
  Inline SVG (like the chevron/check/dots chrome) so it renders in every
  theme via `currentColor` without an IconStore round-trip. Paired with a
  visually-hidden text label for assistive tech.
-->
<ng-template #homeGlyph>
  <span class="tw-page-header-breadcrumbs__home" aria-hidden="true">
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
      <path d="M2.5 7.5L8 3l5.5 4.5M4 6.6V13h8V6.6" />
    </svg>
  </span>
</ng-template>

<!-- separator glyph reused across variants -->
<ng-template #chevronSep>
  <span class="tw-page-header-breadcrumbs__sep" aria-hidden="true">
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      stroke-width="1.65" stroke-linecap="round" stroke-linejoin="round">
      <path d="M6 4l4 4-4 4" />
    </svg>
  </span>
</ng-template>

<!--
  Segment-switcher: a crumb WITH siblings renders a ▾ that opens a
  tw-dropdown of sibling pages (current checkmarked). Shared between the
  explicit `segment` variant AND the auto-promoted default/chevron variant
  (any crumb that carries siblings shows the ▾ inline — no opt-in). Current
  match = `sib.current || sib.label === item.label`.
-->
<ng-template #segmentSwitcher let-item="item" let-isLast="isLast">
  <tw-dropdown placement="bottom">
    <button twDropdownTrigger type="button"
      class="tw-page-header-breadcrumbs__segment"
      [class.tw-page-header-breadcrumbs__current]="isLast">
      {{ item.label }}
      <span class="tw-page-header-breadcrumbs__caret" aria-hidden="true">
        <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor"
          stroke-width="1.65" stroke-linecap="round" stroke-linejoin="round">
          <path d="M4 6l4 4 4-4" />
        </svg>
      </span>
    </button>
    <tw-dropdown-menu>
      @for (sib of item.siblings; track sib.label) {
        @if (sib.current || sib.label === item.label) {
          <tw-dropdown-item [disabled]="true">
            <span class="tw-page-header-breadcrumbs__check" aria-hidden="true">
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor"
                stroke-width="1.65" stroke-linecap="round" stroke-linejoin="round">
                <path d="M3.5 8.5l3 3 6-6.5" />
              </svg>
            </span>
            {{ sib.label }}
          </tw-dropdown-item>
        } @else if (sib.url) {
          <!-- The WHOLE padded dropdown-item row navigates (not just an inner
               text anchor): (selected) fires on click anywhere on the row AND
               on keyboard Enter/Space, and the item auto-closes the menu. -->
          <tw-dropdown-item (selected)="navigateTo(sib)">
            <span class="tw-page-header-breadcrumbs__check" aria-hidden="true"></span>
            <span class="tw-page-header-breadcrumbs__link">{{ sib.label }}</span>
          </tw-dropdown-item>
        } @else {
          <tw-dropdown-item>
            <span class="tw-page-header-breadcrumbs__check" aria-hidden="true"></span>
            {{ sib.label }}
          </tw-dropdown-item>
        }
      }
    </tw-dropdown-menu>
  </tw-dropdown>
</ng-template>

@if (hasItems()) {
  @switch (variant()) {

    <!-- ───────────────────────── slash ───────────────────────── -->
    @case ('slash') {
      @for (item of resolvedItems(); track item.url ?? item.label; let isLast = $last; let isFirst = $first) {
        @if (!isFirst) {
          <span class="tw-page-header-breadcrumbs__sep" aria-hidden="true">/</span>
        }
        @if (isLast || !item.url) {
          <span class="tw-page-header-breadcrumbs__current">{{ item.label }}</span>
        } @else {
          <a class="tw-page-header-breadcrumbs__link" [routerLink]="item.url"
            [queryParams]="item.queryParams ?? null">{{ item.label }}</a>
        }
      }
    }

    <!-- ───────────────────────── icon ───────────────────────── -->
    @case ('icon') {
      @for (item of resolvedItems(); track item.url ?? item.label; let isLast = $last; let isFirst = $first; let i = $index) {
        @if (!isFirst) {
          <ng-container [ngTemplateOutlet]="chevronSep" />
        }
        <span class="tw-page-header-breadcrumbs__crumb"
          [class.tw-page-header-breadcrumbs__current]="isLast || !item.url">
          <span class="tw-page-header-breadcrumbs__icon" aria-hidden="true">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor"
              stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
              <path d="M3 8l5-4 5 4M4.5 7v5.5h7V7" />
            </svg>
          </span>
          @if (isLast || !item.url) {
            {{ item.label }}
          } @else {
            <a class="tw-page-header-breadcrumbs__link" [routerLink]="item.url"
              [queryParams]="item.queryParams ?? null">{{ item.label }}</a>
          }
        </span>
      }
    }

    <!-- ───────────────────────── pill ───────────────────────── -->
    @case ('pill') {
      @for (item of resolvedItems(); track item.url ?? item.label; let isLast = $last; let isFirst = $first) {
        @if (!isFirst) {
          <ng-container [ngTemplateOutlet]="chevronSep" />
        }
        @if (isLast || !item.url) {
          <span class="tw-page-header-breadcrumbs__crumb tw-page-header-breadcrumbs__current">{{ item.label }}</span>
        } @else {
          <a class="tw-page-header-breadcrumbs__crumb tw-page-header-breadcrumbs__link"
            [routerLink]="item.url" [queryParams]="item.queryParams ?? null">{{ item.label }}</a>
        }
      }
    }

    <!-- ───────────────────────── segment ───────────────────────── -->
    @case ('segment') {
      @for (item of resolvedItems(); track item.url ?? item.label; let isLast = $last; let isFirst = $first) {
        @if (!isFirst) {
          <ng-container [ngTemplateOutlet]="chevronSep" />
        }
        @if (item.icon === 'home') {
          <!-- HOME-LED leading crumb (segment variant): the SAME home glyph the
               chevron variant uses, rendered INSIDE the first __segment button
               so it shares the crumb's hover background + the -8px nav
               alignment. Icon-only with a visually-hidden label for a11y. -->
          @if (item.url) {
            <a class="tw-page-header-breadcrumbs__segment tw-page-header-breadcrumbs__segment--static tw-page-header-breadcrumbs__home-crumb tw-page-header-breadcrumbs__link"
              [routerLink]="item.url" [queryParams]="item.queryParams ?? null"
              [attr.aria-label]="item.label">
              <ng-container [ngTemplateOutlet]="homeGlyph" />
              <span class="tw-page-header-breadcrumbs__sr-only">{{ item.label }}</span>
            </a>
          } @else {
            <span class="tw-page-header-breadcrumbs__segment tw-page-header-breadcrumbs__segment--static tw-page-header-breadcrumbs__home-crumb"
              [class.tw-page-header-breadcrumbs__current]="isLast"
              [attr.aria-label]="item.label">
              <ng-container [ngTemplateOutlet]="homeGlyph" />
              <span class="tw-page-header-breadcrumbs__sr-only">{{ item.label }}</span>
            </span>
          }
        } @else if (item.siblings && item.siblings.length > 0) {
          <ng-container
            [ngTemplateOutlet]="segmentSwitcher"
            [ngTemplateOutletContext]="{ item: item, isLast: isLast }" />
        } @else if (isLast || !item.url) {
          <span class="tw-page-header-breadcrumbs__segment tw-page-header-breadcrumbs__segment--static tw-page-header-breadcrumbs__current">{{ item.label }}</span>
        } @else {
          <a class="tw-page-header-breadcrumbs__segment tw-page-header-breadcrumbs__segment--static tw-page-header-breadcrumbs__link"
            [routerLink]="item.url" [queryParams]="item.queryParams ?? null">{{ item.label }}</a>
        }
      }
    }

    <!-- ───────────────────────── collapse ───────────────────────── -->
    @case ('collapse') {
      @if (collapseHead(); as head) {
        @if (head.url) {
          <a class="tw-page-header-breadcrumbs__link" [routerLink]="head.url"
            [queryParams]="head.queryParams ?? null">{{ head.label }}</a>
        } @else {
          <span class="tw-page-header-breadcrumbs__current">{{ head.label }}</span>
        }
      }
      @if (collapseMiddle().length > 0) {
        <ng-container [ngTemplateOutlet]="chevronSep" />
        <tw-dropdown placement="bottom">
          <button twDropdownTrigger type="button"
            class="tw-page-header-breadcrumbs__ellipsis" aria-label="Show hidden breadcrumb segments">
            <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
              <circle cx="3.5" cy="8" r="1.35" fill="currentColor" />
              <circle cx="8" cy="8" r="1.35" fill="currentColor" />
              <circle cx="12.5" cy="8" r="1.35" fill="currentColor" />
            </svg>
          </button>
          <tw-dropdown-menu>
            @for (mid of collapseMiddle(); track mid.url ?? mid.label) {
              @if (mid.url) {
                <!-- Same full-row hit-target fix as the segment-switcher: the
                     padded row navigates via (selected), not an inner anchor. -->
                <tw-dropdown-item (selected)="navigateTo(mid)">
                  <span class="tw-page-header-breadcrumbs__link">{{ mid.label }}</span>
                </tw-dropdown-item>
              } @else {
                <tw-dropdown-item>{{ mid.label }}</tw-dropdown-item>
              }
            }
          </tw-dropdown-menu>
        </tw-dropdown>
      }
      @for (item of collapseTail(); track item.url ?? item.label; let isLast = $last) {
        <ng-container [ngTemplateOutlet]="chevronSep" />
        @if (isLast || !item.url) {
          <span class="tw-page-header-breadcrumbs__current">{{ item.label }}</span>
        } @else {
          <a class="tw-page-header-breadcrumbs__link" [routerLink]="item.url"
            [queryParams]="item.queryParams ?? null">{{ item.label }}</a>
        }
      }
    }

    <!-- ───────────────────────── chevron (default) ───────────────────────── -->
    @default {
      @for (item of resolvedItems(); track item.url ?? item.label; let isLast = $last; let isFirst = $first) {
        @if (!isFirst) {
          <ng-container [ngTemplateOutlet]="chevronSep" />
        }
        @if (item.icon === 'home') {
          <!-- HOME-LED leading crumb: a home glyph (icon-only) with a
               visually-hidden text label. Links to the app root when a url
               is present; renders non-interactive at the root. -->
          @if (item.url) {
            <a class="tw-page-header-breadcrumbs__link tw-page-header-breadcrumbs__home-crumb"
              [routerLink]="item.url" [queryParams]="item.queryParams ?? null"
              [attr.aria-label]="item.label">
              <ng-container [ngTemplateOutlet]="homeGlyph" />
              <span class="tw-page-header-breadcrumbs__sr-only">{{ item.label }}</span>
            </a>
          } @else {
            <span class="tw-page-header-breadcrumbs__home-crumb"
              [class.tw-page-header-breadcrumbs__current]="isLast"
              [attr.aria-label]="item.label">
              <ng-container [ngTemplateOutlet]="homeGlyph" />
              <span class="tw-page-header-breadcrumbs__sr-only">{{ item.label }}</span>
            </span>
          }
        } @else if (item.siblings && item.siblings.length > 0) {
          <!-- AUTO-PROMOTE: any crumb carrying siblings shows the ▾ inline,
               no per-page variant="segment" opt-in required. -->
          <ng-container
            [ngTemplateOutlet]="segmentSwitcher"
            [ngTemplateOutletContext]="{ item: item, isLast: isLast }" />
        } @else if (isLast || !item.url) {
          <span class="tw-page-header-breadcrumbs__current">{{ item.label }}</span>
        } @else {
          <a class="tw-page-header-breadcrumbs__link" [routerLink]="item.url"
            [queryParams]="item.queryParams ?? null">{{ item.label }}</a>
        }
      }
    }
  }
} @else {
  <ng-content></ng-content>
}

<!--
  F19 Wave 7 — TRAILING breadcrumb segment. A consumer projects an inline
  control (e.g. the rich version switcher) via `[breadcrumb-trailing]`; it
  renders as the LAST item in the breadcrumb trail, on the SAME row. The
  chevron separator BEFORE it is a CSS `::before` glyph on `__trailing` (NOT a
  `__sep` DOM element) so it adds no stray separator to consumers that count
  crumbs, and it collapses entirely when no content is projected
  (`:not(:has(*))` in SCSS). Rendered AFTER the variant switch so it always
  trails the last crumb, regardless of variant.
-->
<span class="tw-page-header-breadcrumbs__trailing">
  <ng-content select="[breadcrumb-trailing]"></ng-content>
</span>
